import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

/**
 * Maps backend snake_case color keys to the actual CSS variable names
 * used in `index.css` / shadcn (`--primary`, `--accent`, etc).
 */
const HSL_VAR_MAP = {
  primary: '--primary',
  primary_foreground: '--primary-foreground',
  accent: '--accent',
  accent_foreground: '--accent-foreground',
  success: '--success',
  destructive: '--destructive',
  background: '--background',
  foreground: '--foreground',
  card: '--card',
  border: '--border',
  muted: '--muted',
  muted_foreground: '--muted-foreground',
  ring: '--ring',
};
const RAW_VAR_MAP = {
  gradient_primary: '--gradient-purple',
  gradient_accent: '--gradient-blue',
};

/**
 * Apply a theme's color values to :root. HSL values overwrite shadcn
 * variables; gradients overwrite the legacy gradient variables.
 * The free-form `background_image` is also written directly to <body>
 * as a fixed background so every page gets the ambient gradient/glow.
 */
export function applyPlatformTheme(colors) {
  if (!colors) return;
  const root = document.documentElement;
  Object.entries(HSL_VAR_MAP).forEach(([key, cssVar]) => {
    if (colors[key]) root.style.setProperty(cssVar, colors[key]);
  });
  Object.entries(RAW_VAR_MAP).forEach(([key, cssVar]) => {
    if (colors[key]) root.style.setProperty(cssVar, colors[key]);
  });

  // Split --primary into its H/S/L channels so the theme-aware emerald +
  // yellow Tailwind palettes (defined in tailwind.config.js) can derive
  // shade variations via `calc(var(--primary-l) ± Δ%)`.
  if (colors.primary) {
    const parts = colors.primary.trim().split(/\s+/); // "H S% L%"
    if (parts.length === 3) {
      root.style.setProperty('--primary-h', parts[0]);
      root.style.setProperty('--primary-s', parts[1]);
      root.style.setProperty('--primary-l', parts[2]);
    }
  }
  if (colors.accent) {
    const parts = colors.accent.trim().split(/\s+/);
    if (parts.length === 3) {
      root.style.setProperty('--accent-h', parts[0]);
      root.style.setProperty('--accent-s', parts[1]);
      root.style.setProperty('--accent-l', parts[2]);
    }
  }

  // Global background-image (ambient gradient behind all pages).
  if (colors.background_image && colors.background_image !== 'none') {
    document.body.style.backgroundImage = colors.background_image;
    document.body.style.backgroundAttachment = 'fixed';
    document.body.style.backgroundSize = 'cover';
    document.body.style.backgroundRepeat = 'no-repeat';
    document.body.dataset.platformBackdrop = 'on';
  } else {
    document.body.style.backgroundImage = '';
    document.body.style.backgroundAttachment = '';
    document.body.style.backgroundSize = '';
    document.body.style.backgroundRepeat = '';
    delete document.body.dataset.platformBackdrop;
  }
}

const PlatformThemeContext = React.createContext({
  theme: null,
  loading: true,
  refetch: async () => {},
});

export function PlatformThemeProvider({ children }) {
  const [theme, setTheme] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchActive = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/themes/active`);
      setTheme(data);
      applyPlatformTheme(data?.colors);
    } catch (err) {
      // silent — we keep the index.css defaults
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Defer the initial theme fetch until AFTER the first paint / LCP so it
    // doesn't compete for the critical-path network. Users see the default
    // CSS-baked theme first; any custom admin palette applies a beat later.
    let cancelled = false;
    const runDeferred = () => {
      if (!cancelled) fetchActive();
    };
    let timer = null;
    if (document.readyState === 'complete') {
      timer = setTimeout(runDeferred, 300);
    } else {
      const onLoad = () => {
        timer = setTimeout(runDeferred, 300);
      };
      window.addEventListener('load', onLoad, { once: true });
    }
    // Listen for in-app theme changes (e.g. from the admin themes page).
    const onChange = () => fetchActive();
    window.addEventListener('platform-theme-changed', onChange);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      window.removeEventListener('platform-theme-changed', onChange);
    };
  }, [fetchActive]);

  return (
    <PlatformThemeContext.Provider value={{ theme, loading, refetch: fetchActive }}>
      {children}
    </PlatformThemeContext.Provider>
  );
}

export function usePlatformTheme() {
  return React.useContext(PlatformThemeContext);
}
